import { useRef, useState } from 'react';
import {
  ChevronRight,
  Clock,
  GraduationCap,
  Infinity as InfinityIcon,
  Lock,
  Mail,
  Maximize2,
  MonitorSmartphone,
  Play,
  QrCode,
  Zap,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Lightbox from '../components/Lightbox';
import Markdown from '../components/Markdown';
import ProductPreview from '../components/ProductPreview';
import VideoPlayer from '../components/VideoPlayer';
import { galleryImageUrl, type Lesson, type ProductDetail } from '../lib/api';
import {
  CHALK_SWATCH,
  contentTypeLabel,
  formatClock,
  formatDuration,
  formatPrice,
  isFree,
  subjectLabel,
} from '../lib/format';
import { useProduct, useSample } from '../lib/queries';

/** Odscrolluje k prvku podle id, plynule jen když uživatel nevypnul animace. */
function scrollToId(id: string) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
}

/* Galerie náhledů. Reálné GALLERY assety se ukazují jako obrázky na světlém
 * podkladu; hlavní náhled je klikací a otevírá lightbox. Bez obrázků padáme na
 * tabulový fallback s kresleným glyfem. Klik na miniaturu přepne hlavní plochu. */

function Gallery({ product }: { product: ProductDetail }) {
  const images = product.gallery;
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const activeIndex = images.length > 0 ? Math.min(active, images.length - 1) : 0;
  const activeImage = images[activeIndex];

  /* Nastaví index a otevře lightbox. */
  const openLightbox = (i: number) => {
    setActive(i);
    setLightbox(true);
  };

  /* Po zavření lightboxu vrať focus zpět na spouštěcí náhled. */
  const closeLightbox = () => {
    setLightbox(false);
    triggerRef.current?.focus();
  };

  // Produkt bez obrázků: tabulový fallback s kresleným glyfem.
  if (!activeImage) {
    return (
      <div className="board grid aspect-square place-items-center overflow-hidden rounded-card p-6 shadow-card md:p-10">
        <ProductPreview
          subject={product.subject}
          contentType={product.content_type}
          productId={product.id}
          className="h-auto max-h-[72%] w-[64%]"
        />
      </div>
    );
  }

  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => openLightbox(activeIndex)}
        aria-label="Zvětšit náhled"
        className="focus-ring group relative block w-full cursor-zoom-in overflow-hidden rounded-card border border-line bg-card shadow-card"
      >
        <img
          src={galleryImageUrl(product.slug, activeImage.id)}
          alt={`Náhled ${activeIndex + 1}: ${product.title}`}
          className="aspect-square w-full object-cover"
        />
        <span className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-board-deep/70 text-chalk backdrop-blur transition group-hover:bg-board-deep">
          <Maximize2 className="h-4 w-4" strokeWidth={2} />
        </span>
      </button>

      {images.length > 1 && (
        <div className="mt-3 flex gap-2.5">
          {images.map((thumb, i) => {
            const isActive = activeIndex === i;
            return (
              <button
                key={thumb.id}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Náhled ${i + 1}`}
                aria-current={isActive}
                className={`focus-ring h-20 w-20 overflow-hidden rounded-lg border bg-card transition ${
                  isActive
                    ? 'border-board ring-2 ring-board/50'
                    : 'border-line opacity-80 hover:opacity-100'
                }`}
              >
                <img
                  src={galleryImageUrl(product.slug, thumb.id)}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </button>
            );
          })}
        </div>
      )}

      {lightbox && (
        <Lightbox
          images={images}
          index={activeIndex}
          slug={product.slug}
          title={product.title}
          onIndexChange={setActive}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
}

/* Osnova lekcí videokurzu */

function LessonList({ lessons }: { lessons: Lesson[] }) {
  const sorted = [...lessons].sort((a, b) => a.order_index - b.order_index);
  const totalS = sorted.reduce((sum, l) => sum + l.duration_s, 0);

  const scrollToPlayer = () => scrollToId('ukazka');

  return (
    <section className="mt-8">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-xl">Obsah kurzu</h2>
        <span className="text-sm text-ink-soft">
          {sorted.length} lekcí · {formatDuration(totalS)}
        </span>
      </div>
      <ol className="mt-3 divide-y divide-line overflow-hidden rounded-card border border-line bg-card shadow-card">
        {sorted.map((lesson) => (
          <li
            key={lesson.id}
            className={`flex items-center gap-3 p-4 ${lesson.is_sample ? 'bg-accent-soft/40' : ''}`}
          >
            <span
              className={`grid h-10 w-10 flex-none place-items-center rounded-xl ${
                lesson.is_sample ? 'bg-accent text-ink' : 'bg-paper2 text-ink-soft'
              }`}
            >
              {lesson.is_sample ? (
                <Play className="h-5 w-5" strokeWidth={2} fill="currentColor" />
              ) : (
                <Lock className="h-4 w-4" strokeWidth={2} />
              )}
            </span>
            <div className="min-w-0 grow">
              <p className="font-medium leading-snug">
                {lesson.order_index}. {lesson.title}
              </p>
              <p
                className={`text-sm ${lesson.is_sample ? 'font-semibold text-board' : 'text-ink-soft'}`}
              >
                {lesson.is_sample ? 'Ukázková lekce · ' : ''}
                {formatClock(lesson.duration_s)}
              </p>
            </div>
            {lesson.is_sample && (
              <button
                type="button"
                onClick={scrollToPlayer}
                className="h-10 flex-none rounded-xl border border-board/30 bg-card px-4 text-sm font-bold text-board transition hover:bg-accent-soft"
              >
                Přehrát
              </button>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

/* Nákupní panel */

function fulfillmentLines(product: ProductDetail) {
  const isPdf = product.content_type === 'PDF';
  switch (product.fulfillment_type) {
    case 'STREAM':
      return [
        { icon: MonitorSmartphone, text: 'Streamování na mobilu, tabletu i počítači' },
        { icon: Clock, text: 'Přístup obvykle během pár minut po zaplacení' },
        { icon: QrCode, text: 'QR platba převodem bez poplatku' },
      ];
    case 'LICENSE_KEY':
      return [
        { icon: Mail, text: 'Licenční klíč přijde e-mailem obvykle během pár minut' },
        { icon: InfinityIcon, text: 'Aktivace na vašem počítači' },
        { icon: QrCode, text: 'QR platba převodem bez poplatku' },
      ];
    case 'DOWNLOAD':
      return [
        {
          icon: Mail,
          text: 'E-mailem přijde odkaz na stránku objednávky, odkud soubor stáhnete, obvykle během pár minut po zaplacení',
        },
        {
          icon: InfinityIcon,
          text: isPdf
            ? 'Soubor je váš napořád, k tisku pro vlastní a výukovou potřebu'
            : 'Soubor je váš napořád',
        },
        { icon: QrCode, text: 'QR platba převodem bez poplatku' },
      ];
    case 'EMAIL':
    default:
      return [
        { icon: Mail, text: 'Soubor přijde přímo e-mailem obvykle během pár minut po zaplacení' },
        {
          icon: InfinityIcon,
          text: isPdf
            ? 'Soubor je váš napořád, k tisku pro vlastní a výukovou potřebu'
            : 'Soubor je váš napořád',
        },
        { icon: QrCode, text: 'QR platba převodem bez poplatku' },
      ];
  }
}

function BuyPanel({ product }: { product: ProductDetail }) {
  const isVideo = product.content_type === 'VIDEO';
  const isPdf = product.content_type === 'PDF';
  const free = isFree(product.price_czk);
  const buyLabel = isVideo ? 'Získat přístup ke kurzu' : 'Koupit materiál';

  // Video: skok k přehrávači s ukázkovou lekcí.
  const onSample = () => scrollToId('ukazka');

  return (
    <div className="lg:sticky lg:top-20 lg:space-y-4">
      <div className="mb-4 flex flex-wrap gap-2 lg:mb-0">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-paper2 px-2.5 py-1 text-xs font-semibold text-ink">
          <span
            className={`h-2.5 w-2.5 rounded-full ring-1 ring-line ${CHALK_SWATCH[product.subject.color]}`}
            aria-hidden="true"
          />
          {subjectLabel(product.subject)}
        </span>
        {product.grade && (
          <span className="rounded-full bg-paper2 px-2.5 py-1 text-xs font-semibold text-ink">
            {product.grade}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 rounded-full bg-board px-2.5 py-1 text-xs font-semibold text-chalk">
          {isVideo && <Play className="h-3 w-3" strokeWidth={2.5} fill="currentColor" />}
          {contentTypeLabel(product.content_type)}
        </span>
      </div>

      <div>
        <h1 className="text-[26px] leading-tight md:text-3xl">{product.title}</h1>
        {product.subtitle && <p className="mt-2 text-ink-soft">{product.subtitle}</p>}
      </div>

      <div className="mt-4 rounded-card border border-line bg-card p-5 shadow-card lg:mt-0">
        <div className="flex items-baseline gap-2">
          <span className={`text-3xl font-bold tabular-nums ${free ? 'text-success' : 'text-ink'}`}>
            {formatPrice(product.price_czk)}
          </span>
          {!free && <span className="text-sm text-ink-soft">jednorázově</span>}
        </div>

        <Link
          to={`/pokladna/nova?produkt=${encodeURIComponent(product.slug)}`}
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-board font-semibold text-chalk shadow-card transition hover:bg-board-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent active:scale-[.98]"
        >
          <Zap className="h-5 w-5" strokeWidth={2} />
          {buyLabel}
        </Link>

        {product.has_sample && isVideo && (
          <button
            type="button"
            onClick={onSample}
            className="mt-2.5 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-line bg-card font-semibold text-ink transition hover:border-board focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-board/50 active:scale-[.98]"
          >
            <Play className="h-5 w-5" strokeWidth={2} /> Přehrát ukázku
          </button>
        )}

        <div className="mt-4 space-y-2 text-sm text-ink-soft">
          {fulfillmentLines(product).map(({ icon: Icon, text }) => (
            <p key={text} className="flex items-center gap-2">
              <Icon className="h-4 w-4 flex-none text-board" strokeWidth={2} />
              {text}
            </p>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-card border border-line bg-paper2 p-4 lg:mt-0">
        <GraduationCap className="mt-0.5 h-6 w-6 flex-none text-board" strokeWidth={2} />
        <p className="text-sm text-ink-soft">
          Materiály tvořím tak, jak je sama potřebuji pro své žáky.{' '}
          {isPdf
            ? 'Jsou hned připravené k tisku, bez zbytečné omáčky kolem.'
            : 'Jsou hned připravené k použití, bez zbytečné omáčky kolem.'}
        </p>
      </div>
    </div>
  );
}

/* Stránka */

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 lg:grid lg:grid-cols-2 lg:gap-10">
      <div>
        <div className="aspect-square rounded-card bg-paper2 motion-safe:animate-pulse" />
      </div>
      <div className="mt-6 space-y-4 lg:mt-0">
        <div className="h-8 w-3/4 rounded bg-paper2 motion-safe:animate-pulse" />
        <div className="h-4 w-full rounded bg-paper2 motion-safe:animate-pulse" />
        <div className="h-40 w-full rounded-card bg-paper2 motion-safe:animate-pulse" />
      </div>
    </div>
  );
}

export default function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: product, isLoading, isError, error } = useProduct(slug);
  const { data: sample } = useSample(slug);

  if (isLoading) {
    return (
      <div className="grid-paper">
        <DetailSkeleton />
      </div>
    );
  }

  if (isError || !product) {
    const notFound =
      error &&
      typeof error === 'object' &&
      'status' in error &&
      (error as { status: number }).status === 404;
    return (
      <div className="grid-paper">
        <div className="mx-auto max-w-2xl px-4 py-20 text-center">
          <h1 className="text-3xl">{notFound ? 'Materiál nenalezen' : 'Něco se pokazilo'}</h1>
          <p className="mt-3 text-ink-soft">
            {notFound
              ? 'Tento materiál neexistuje nebo byl stažen z nabídky.'
              : 'Detail se nepodařilo načíst. Zkuste to prosím za chvíli znovu.'}
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex h-12 items-center rounded-xl bg-board px-6 font-semibold text-chalk transition hover:bg-board-deep"
          >
            Zpět do katalogu
          </Link>
        </div>
      </div>
    );
  }

  const isVideo = product.content_type === 'VIDEO';
  const category = product.categories[0];
  const sampleLesson = sample?.sample_lessons?.[0] ?? product.lessons.find((l) => l.is_sample);

  return (
    <div className="grid-paper pb-24 md:pb-0">
      <div className="mx-auto max-w-6xl px-4">
        <nav
          className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap py-4 text-sm text-ink-soft"
          aria-label="Drobečková navigace"
        >
          <Link to="/" className="py-1 hover:text-ink">
            Katalog
          </Link>
          {category && (
            <>
              <ChevronRight className="h-4 w-4 flex-none" strokeWidth={2} />
              <Link to={`/kategorie/${category.slug}`} className="py-1 hover:text-ink">
                {category.name}
              </Link>
            </>
          )}
          <ChevronRight className="h-4 w-4 flex-none" strokeWidth={2} />
          <span className="truncate font-medium text-ink">{product.title}</span>
        </nav>

        <div className="lg:grid lg:grid-cols-2 lg:gap-10">
          <section>
            {isVideo ? (
              <div id="ukazka" className="scroll-mt-24">
                <VideoPlayer
                  title={sampleLesson?.title ?? product.title}
                  durationS={sampleLesson?.duration_s}
                />
              </div>
            ) : (
              <div id="ukazka" className="scroll-mt-24">
                <Gallery product={product} />
              </div>
            )}

            {product.categories.some((c) => c.slug === 'vyukove-prehledy') && (
              <p className="mt-3 rounded-lg border border-line bg-paper2/60 px-4 py-3 text-sm text-ink-soft">
                Náhledové obrázky mají podobu prezentace na&nbsp;výšku, samotné přehledy jsou však
                zpracované na&nbsp;šířku, aby byly mnohem přehlednější. Tuto podobu náhledů jsme
                zvolili záměrně, aby podklady nemohla zneužít umělá inteligence.
              </p>
            )}

            {isVideo && product.lessons.length > 0 && <LessonList lessons={product.lessons} />}

            <section className="mt-8 hidden lg:block">
              <h2 className="text-xl md:text-2xl">O materiálu</h2>
              <div className="mt-3 rounded-card border border-line bg-card p-5 shadow-card md:p-7">
                <Markdown source={product.description_md} />
              </div>
            </section>
          </section>

          <section className="mt-6 lg:mt-0">
            <BuyPanel product={product} />
          </section>
        </div>

        <section className="mt-8 lg:hidden">
          <h2 className="text-xl md:text-2xl">O materiálu</h2>
          <div className="mt-3 rounded-card border border-line bg-card p-5 shadow-card md:p-7">
            <Markdown source={product.description_md} />
          </div>
        </section>
      </div>

      {/* Sticky spodní CTA (jen mobil) */}
      <div className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-line bg-paper/95 backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <div className="shrink-0">
            <p
              className={`text-xl font-bold tabular-nums leading-none ${
                isFree(product.price_czk) ? 'text-success' : 'text-ink'
              }`}
            >
              {formatPrice(product.price_czk)}
            </p>
            <p className="mt-0.5 text-[11px] text-ink-soft">
              {isVideo ? 'přístup do pár minut' : 'doručení do pár minut'}
            </p>
          </div>
          <Link
            to={`/pokladna/nova?produkt=${encodeURIComponent(product.slug)}`}
            className="flex h-12 grow items-center justify-center gap-2 rounded-xl bg-board font-semibold text-chalk transition active:scale-[.98]"
          >
            <Zap className="h-5 w-5" strokeWidth={2} />
            {isVideo ? 'Získat přístup ke kurzu' : 'Koupit materiál'}
          </Link>
        </div>
      </div>
    </div>
  );
}
